import {
  SearchField as CoreSearchField,
  SearchFieldProps as CoreSearchFieldProps,
} from "./internal/SearchField";
import { forwardRef } from "react";
import { DistributiveOmit } from "../../types";

/**
 * Props for the SearchField component
 * @extends Omit<ComponentPropsWithoutRef<"input">, "size" | "prefix" | "required" | "children">
 * @extends LayoutUtilProps
 */
export type SearchFieldProps = DistributiveOmit<
  CoreSearchFieldProps,
  "size"
> & {
  /**
   * Size of the search field
   * @default "medium"
   */
  size?: Extract<CoreSearchFieldProps["size"], "small" | "medium" | "large">;
};

/**
 * SearchField component for text input with search functionality.
 *
 * Features:
 * - Search icon prefix for visual clarity
 * - Clear button that appears when input has value
 * - Arrow key navigation between input and clear button (Right arrow to clear button, Left arrow back to input)
 * - Clear button activated with Enter/Space keys
 * - Focus returns to input after clearing
 * - Multiple size variants (small, medium, large)
 * - Controlled and uncontrolled modes
 * - Disabled and read-only states
 * - Accessibility support with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Automatic value detection for clear button visibility
 * - Search-specific input type and autocomplete settings
 * - Screen reader support with placeholder announcements
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <SearchField
 *   placeholder="Search users..."
 *   onChange={(e) => setSearchTerm(e.target.value)}
 *   onClear={() => setSearchTerm("")}
 * />
 *
 * @example
 * <SearchField
 *   size="large"
 *   value={searchValue}
 *   onChange={(e) => setSearchValue(e.target.value)}
 *   disabled={isLoading}
 * />
 */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  (props, ref) => {
    return <CoreSearchField ref={ref} {...props} />;
  },
);
SearchField.displayName = "SearchField";
